/**
 * Phase 126 — Premium Slot Calendar (Linear/Notion-grade scheduling grid).
 *
 * Renders a 7-day × N-hour grid:
 *   - Vertical axis = hour of day (06:00 → 23:00, configurable)
 *   - Horizontal axis = 7 dates (today + next 6 days)
 *   - One grid PER court (operator switches via the left sidebar)
 *
 * Slot states + visual language:
 *   available   → neutral surface
 *   blocked     → muted slate, diagonal stripe
 *   requested   → amber
 *   confirmed   → emerald
 *   live        → cyan pulse
 *   completed   → muted emerald
 *   peak hour   → subtle gold rim
 *
 * Interactions:
 *   - Click an available slot → bulk-confirm / bulk-block via menu
 *   - Click a confirmed slot  → view + status menu
 *   - Drag (vertical) to bulk-toggle a column range (future iteration)
 */
import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { trackContextMenu } from '@/lib/analytics';
import { BulletproofContextMenu } from '@/components/ui/bulletproof-context-menu';
import MaintenanceModeDialog from '@/components/arenax/dialogs/MaintenanceModeDialog';
import PeakPricingDialog from '@/components/arenax/dialogs/PeakPricingDialog';
import { SlotAuditDrawer } from '@/components/arenax/LifecycleAuditDrawer';
import {
  ChevronLeft, ChevronRight, RefreshCcw, Loader2, Lock, Unlock, CheckCircle2,
  Eye, Users, Copy, Sparkles, IndianRupee, X as XIcon, Wrench, RotateCcw, Clock,
  History,
} from 'lucide-react';

const STATE_STYLES = {
  available: { bg: 'var(--ax-card-bg-soft)', fg: 'hsl(var(--ax-text-muted))', border: 'var(--ax-border-quiet)' },
  blocked:   { bg: 'repeating-linear-gradient(45deg, var(--ax-card-bg-soft), var(--ax-card-bg-soft) 4px, transparent 4px, transparent 8px)', fg: 'hsl(var(--ax-text-faint))', border: 'var(--ax-border-quiet)' },
  requested: { bg: 'rgba(245,158,11,0.18)', fg: '#92400e', border: 'rgba(245,158,11,0.35)' },
  confirmed: { bg: 'rgba(16,185,129,0.20)', fg: '#047857', border: 'rgba(16,185,129,0.40)' },
  live:      { bg: 'rgba(6,182,212,0.30)', fg: '#0e7490', border: 'rgba(6,182,212,0.55)', pulse: true },
  completed: { bg: 'rgba(16,185,129,0.08)', fg: '#065f46', border: 'rgba(16,185,129,0.18)' },
};

function dateOnly(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function fmtDay(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}
function fmtDate(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function fmtHour(h) {
  const ap = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh} ${ap}`;
}

export default function ArenaxSlotCalendar({
  venueId, courts, selectedCourtId, onSelectCourt,
  onRequestGenerate, onRefresh, busy,
}) {
  const [weekStart, setWeekStart] = useState(() => dateOnly(new Date()));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selected, setSelected] = useState(null);  // selected slot row
  // Phase 178.6.G Part 4 — premium slot-side dialogs
  const [maintenanceSlot, setMaintenanceSlot] = useState(null);
  const [peakPricingSlot, setPeakPricingSlot] = useState(null);
  // StreamX Sprint 3b — slot lifecycle audit drawer
  const [auditSlot, setAuditSlot] = useState(null);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const hours = useMemo(() => Array.from({ length: 17 }, (_, i) => i + 6), []); // 6..22

  // Fetch slots for the visible week + current court
  const loadSlots = async () => {
    if (!venueId || !selectedCourtId) { setSlots([]); return; }
    setLoadingSlots(true);
    try {
      const end = new Date(weekStart);
      end.setDate(end.getDate() + 7);
      const params = new URLSearchParams({
        court_id: selectedCourtId,
        start: weekStart.toISOString(),
        end: end.toISOString(),
      });
      const r = await api.get(`/arenax/host/venue/${venueId}/slots?${params}`);
      setSlots(r.data?.slots || []);
    } catch (_e) { setSlots([]); }
    finally { setLoadingSlots(false); }
  };

  // Auto-load when court/week changes
  useEffect(() => { loadSlots(); /* eslint-disable-next-line */ }, [venueId, selectedCourtId, weekStart]);

  // Build a lookup: { 'YYYY-MM-DD|HH': slot }
  const slotMap = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const d = new Date(s.start_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}|${d.getHours()}`;
      m.set(key, s);
    }
    return m;
  }, [slots]);

  const mutateSlot = async (slot, state) => {
    try {
      await api.patch(`/arenax/host/venue/${venueId}/slots/${slot.id}`, { state });
      setSlots(prev => prev.map(s => s.id === slot.id ? { ...s, state } : s));
      setSelected(null);
      toast.success(`Slot → ${state}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  /**
   * Phase 178.6 — operator action surface for a single slot cell.
   *
   * State-aware: surfaces only actions that make sense for the slot's
   * current lifecycle phase.  Actions without a backend endpoint yet
   * render as `disabled: true` to preserve the visual intent hierarchy
   * and tease future capability surface.
   *
   * Backend-wired now: Block (warning→state=blocked), Unblock
   * (success→state=available), Confirm (success→state=confirmed for
   * requested slots).
   *
   * Backend-pending (disabled): View bookings, Duplicate slot, Extend
   * session, Hold slot, Peak pricing, Maintenance mode, Refund all bookings.
   */
  const buildSlotActionItems = (slot) => {
    const isAvailable = slot.state === 'available';
    const isBlocked   = slot.state === 'blocked';
    const isRequested = slot.state === 'requested';
    const isConfirmed = slot.state === 'confirmed';
    const isLive      = slot.state === 'live';
    const isCompleted = slot.state === 'completed';
    const isImmutable = isLive || isCompleted;
    return [
      // ── NEUTRAL ────────────────────────────────────────────────
      { id: 'view-slot', label: 'View slot', icon: Eye,
        action: () => setSelected(slot), variant: 'neutral' },
      { id: 'view-history', label: 'View history', icon: History,
        action: () => setAuditSlot(slot), variant: 'neutral' },
      { id: 'view-bookings', label: 'View bookings', icon: Users,
        disabled: true, variant: 'neutral' },
      { id: 'duplicate', label: 'Duplicate slot', icon: Copy,
        disabled: true, variant: 'neutral' },
      { separator: true },

      // ── SUCCESS ────────────────────────────────────────────────
      isBlocked && !isImmutable && {
        id: 'unblock', label: 'Open availability', icon: Unlock,
        action: () => mutateSlot(slot, 'available'), variant: 'success' },
      isRequested && {
        id: 'confirm', label: 'Confirm booking', icon: CheckCircle2,
        action: () => mutateSlot(slot, 'confirmed'), variant: 'success' },
      (isConfirmed || isAvailable) && !isImmutable && {
        id: 'extend', label: 'Extend session', icon: Clock,
        disabled: true, variant: 'success' },

      // ── WARNING ────────────────────────────────────────────────
      isAvailable && {
        id: 'hold', label: 'Hold slot', icon: Sparkles,
        disabled: true, variant: 'warning' },
      isAvailable && {
        id: 'peak', label: 'Set peak pricing', icon: IndianRupee,
        action: () => setPeakPricingSlot(slot), variant: 'warning' },
      { separator: true },

      // ── DANGER ────────────────────────────────────────────────
      (isAvailable || isRequested) && !isImmutable && {
        id: 'block', label: 'Cancel slot', icon: XIcon,
        action: () => mutateSlot(slot, 'blocked'), variant: 'danger' },
      !isImmutable && {
        id: 'maintenance', label: 'Maintenance mode', icon: Wrench,
        action: () => setMaintenanceSlot(slot), variant: 'danger' },
      isConfirmed && {
        id: 'refund-all', label: 'Refund all bookings', icon: RotateCcw,
        disabled: true, variant: 'danger' },
    ].filter(Boolean);
  };

  return (
    <div className="space-y-3" data-testid="arenax-slot-calendar">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
            className="h-8 px-2 rounded-lg text-xs flex items-center gap-1 transition-colors"
            style={{ color: 'hsl(var(--ax-text-muted))', background: 'var(--ax-card-bg-soft)', border: '1px solid var(--ax-border-quiet)' }}
            data-testid="slot-cal-prev"
          ><ChevronLeft className="h-3.5 w-3.5" /> Prev week</button>
          <div className="text-xs font-bold tracking-wide" style={{ color: 'hsl(var(--ax-text))' }}>
            {fmtDate(days[0])} – {fmtDate(days[6])}
          </div>
          <button
            type="button"
            onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
            className="h-8 px-2 rounded-lg text-xs flex items-center gap-1"
            style={{ color: 'hsl(var(--ax-text-muted))', background: 'var(--ax-card-bg-soft)', border: '1px solid var(--ax-border-quiet)' }}
            data-testid="slot-cal-next"
          >Next week <ChevronRight className="h-3.5 w-3.5" /></button>
          <button
            type="button"
            onClick={() => setWeekStart(dateOnly(new Date()))}
            className="h-8 px-3 rounded-lg text-xs font-semibold"
            style={{ color: 'hsl(var(--ax-text-muted))' }}
            data-testid="slot-cal-today"
          >Today</button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { onRefresh?.(); loadSlots(); }}
            disabled={loadingSlots}
            className="h-8 px-2 rounded-lg text-xs flex items-center gap-1"
            style={{ color: 'hsl(var(--ax-text-muted))', background: 'var(--ax-card-bg-soft)', border: '1px solid var(--ax-border-quiet)' }}
            data-testid="slot-cal-refresh"
          >
            {loadingSlots ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
            Refresh
          </button>
          <button
            type="button"
            onClick={onRequestGenerate}
            disabled={busy}
            className="h-8 px-3 rounded-lg text-xs font-bold transition-all hover:-translate-y-0.5 disabled:opacity-60"
            style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
            data-testid="slot-cal-generate"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> : null}
            Generate next 14 days
          </button>
        </div>
      </div>

      {/* Court switcher (chips, horizontal scroll) */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="slot-cal-court-switcher">
        {courts.length === 0 && (
          <p className="text-xs italic" style={{ color: 'hsl(var(--ax-text-faint))' }}>
            Add at least one court below before setting up slots.
          </p>
        )}
        {courts.map(c => {
          const active = c.id === selectedCourtId;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelectCourt(c.id)}
              className="text-[11px] font-bold px-3 py-1.5 rounded-full whitespace-nowrap transition-all"
              style={{
                background: active ? 'var(--ax-primary-gradient)' : 'var(--ax-card-bg-soft)',
                color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-text-muted))',
                border: active ? 'none' : '1px solid var(--ax-border-quiet)',
              }}
              data-testid={`slot-cal-court-${c.id}`}
            >
              {c.name}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {selectedCourtId && (
        <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--ax-border-quiet)' }}>
          <div className="grid" style={{ gridTemplateColumns: '64px repeat(7, 1fr)', minWidth: 720 }}>
            {/* Header row */}
            <div className="text-[10px] font-bold px-2 py-2 uppercase tracking-wider"
                 style={{ color: 'hsl(var(--ax-text-faint))', background: 'var(--ax-card-bg)' }}>
              Hour
            </div>
            {days.map((d, i) => {
              const isToday = dateOnly(d).getTime() === dateOnly(new Date()).getTime();
              return (
                <div
                  key={i}
                  className="text-[10px] font-bold px-2 py-2 text-center uppercase tracking-wider"
                  style={{
                    color: isToday ? 'hsl(var(--ax-accent-cyan))' : 'hsl(var(--ax-text-faint))',
                    background: 'var(--ax-card-bg)',
                    borderLeft: '1px solid var(--ax-divider)',
                  }}
                >
                  <div>{fmtDay(d)}</div>
                  <div style={{ color: isToday ? 'hsl(var(--ax-accent-cyan))' : 'hsl(var(--ax-text-muted))' }}>
                    {fmtDate(d)}
                  </div>
                </div>
              );
            })}

            {/* Hour rows */}
            {hours.map((h, hi) => (
              <Row
                key={hi}
                hour={h}
                days={days}
                slotMap={slotMap}
                onClickSlot={setSelected}
                buildSlotActionItems={buildSlotActionItems}
                venueId={venueId}
              />
            ))}
          </div>
        </div>
      )}

      {/* Selected-slot mutation menu */}
      {selected && (
        <div className="fixed bottom-6 inset-x-0 flex justify-center z-50 px-4">
          <div
            className="arenax-card rounded-xl px-4 py-3 flex items-center gap-3 shadow-2xl"
            data-testid="slot-cal-action-menu"
          >
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold"
                   style={{ color: 'hsl(var(--ax-text-faint))' }}>
                {new Date(selected.start_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric' })}
              </div>
              <div className="text-xs font-bold" style={{ color: 'hsl(var(--ax-text))' }}>
                {selected.state} {selected.price ? `· ₹${selected.price}` : ''} {selected.is_peak ? '· Peak' : ''}
              </div>
            </div>
            {selected.state !== 'blocked' && (
              <button
                onClick={() => mutateSlot(selected, 'blocked')}
                className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"
                style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}
                data-testid="slot-cal-action-block"
              ><Lock className="h-3 w-3" /> Block</button>
            )}
            {selected.state === 'blocked' && (
              <button
                onClick={() => mutateSlot(selected, 'available')}
                className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"
                style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}
                data-testid="slot-cal-action-unblock"
              ><Unlock className="h-3 w-3" /> Unblock</button>
            )}
            {selected.state === 'requested' && (
              <button
                onClick={() => mutateSlot(selected, 'confirmed')}
                className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 font-bold"
                style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                data-testid="slot-cal-action-confirm"
              ><CheckCircle2 className="h-3 w-3" /> Confirm</button>
            )}
            <button
              onClick={() => setSelected(null)}
              className="text-xs px-2 py-1.5 rounded-lg"
              style={{ color: 'hsl(var(--ax-text-muted))' }}
              data-testid="slot-cal-action-close"
            >Close</button>
          </div>
        </div>
      )}

      {/* Phase 178.6.G Part 4 — premium slot-side dialogs */}
      <MaintenanceModeDialog
        slot={maintenanceSlot}
        open={!!maintenanceSlot}
        onClose={() => setMaintenanceSlot(null)}
        onSuccess={() => { setMaintenanceSlot(null); loadSlots(); }}
      />
      <PeakPricingDialog
        slot={peakPricingSlot}
        open={!!peakPricingSlot}
        onClose={() => setPeakPricingSlot(null)}
        onSuccess={() => { setPeakPricingSlot(null); loadSlots(); }}
      />
      {/* StreamX Sprint 3b — slot lifecycle history */}
      <SlotAuditDrawer
        open={!!auditSlot}
        onClose={() => setAuditSlot(null)}
        entity={auditSlot}
      />
    </div>
  );
}

function Row({ hour, days, slotMap, onClickSlot, buildSlotActionItems, venueId }) {
  return (
    <>
      <div
        className="text-[10px] font-bold px-2 py-2 text-right"
        style={{ color: 'hsl(var(--ax-text-faint))', borderTop: '1px solid var(--ax-divider)', background: 'var(--ax-card-bg)' }}
      >
        {fmtHour(hour)}
      </div>
      {days.map((d, i) => {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}|${hour}`;
        const slot = slotMap.get(key);
        const style = STATE_STYLES[slot?.state] || { bg: 'transparent', fg: 'hsl(var(--ax-text-faint))', border: 'transparent' };

        const cellInner = (
          <div
            className="relative h-12 transition-colors cursor-pointer hover:opacity-90"
            style={{
              background: slot ? style.bg : 'transparent',
              borderTop: '1px solid var(--ax-divider)',
              borderLeft: '1px solid var(--ax-divider)',
              boxShadow: slot?.is_peak ? 'inset 0 0 0 1px rgba(217,119,6,0.35)' : 'none',
            }}
            onClick={() => slot && onClickSlot(slot)}
            data-testid={slot ? `slot-cell-${slot.id}` : undefined}
          >
            {slot && (
              <div
                className="absolute inset-1 rounded-md flex items-center justify-center text-[10px] font-bold tracking-wide"
                style={{
                  color: style.fg,
                  background: slot.state === 'available' ? 'transparent' : style.bg,
                  border: `1px solid ${style.border}`,
                  animation: style.pulse ? 'arenax-pulse-cyan 2s ease-in-out infinite' : undefined,
                }}
              >
                {slot.state === 'available' && slot.price ? `₹${slot.price}` : slot.state === 'available' ? '' : slot.state.toUpperCase()}
              </div>
            )}
          </div>
        );

        // Phase 178.6 — only slots with actual data get the context menu.
        // Empty cells (no slot data) stay click-inert so right-click selection
        // / browser default behavior still works there.
        if (!slot) {
          return <div key={i}>{cellInner}</div>;
        }

        return (
          <BulletproofContextMenu
            key={i}
            items={buildSlotActionItems(slot)}
            metadata={{
              entity_type: 'venue_slot',
              row_id: slot.id,
              slot_state: slot.state,
              venue_id: venueId,
              is_peak: !!slot.is_peak,
            }}
            onMenuOpen={(m) => trackContextMenu('opened', m)}
            onActionSelected={(item, m) => trackContextMenu('selected', {
              ...m, action_id: item.id, action_label: item.label, variant: item.variant,
            })}
            onCancelled={(m) => trackContextMenu('cancelled', m)}
          >
            {cellInner}
          </BulletproofContextMenu>
        );
      })}
    </>
  );
}
